import React, { useState, useEffect } from "react";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import { Table, Button, Modal, Tag, Switch, Spin } from "antd";
import moment from "moment";
import BackButton from "../../../../../components/buttons/BackButton";

import { fetchDailySummary } from "../../../../../services/report.service";

const { RangePicker } = DatePicker;

const InstructorReportStatus = () => {
  const [dateRange, setDateRange] = useState([
    moment().subtract(7, "days"),
    moment(),
  ]);
  const [dates, setDates] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [isLiveView, setIsLiveView] = useState(false);
  const [isFetchingSub, setIsFetchingSub] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const fetchData = async (start, end) => {
    setLoading(true);
    setIsFetchingSub(true);
    const curr = start.clone();
    const promises = [];

    while (curr.isSameOrBefore(end, "day")) {
      const dateStr = curr.format("YYYY-MM-DD");
      promises.push(
        fetchDailySummary(dateStr).catch((err) => {
          console.error(`Failed to fetch summary for ${dateStr}`, err);
          return null;
        }),
      );
      curr.add(1, "days");
    }

    try {
      const results = await Promise.all(promises);

      const formattedData = results
        .filter((res) => res && res.success)
        .map((res) => {
          const d = res.data;
          return {
            date: d.meta.date,
            day: d.meta.day,
            attendance: {
              onTime: d.summary.on_time,
              missed: d.summary.missed,
              shortTime: d.summary.short_time,
              leave: d.summary.leave,
              sick: d.summary.sick,
              holiday: d.summary.holiday,
              checkIn: d.summary.check_in,
              checkOut: d.summary.check_out,
              details: d.details,
            },
          };
        })
        .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());

      setData(formattedData);
    } catch (e) {
      console.error("Error fetching summaries", e);
    } finally {
      setLoading(false);
      setIsFetchingSub(false);
    }
  };

  useEffect(() => {
    let intervalId;
    if (dateRange && dateRange[0] && dateRange[1]) {
      fetchData(dateRange[0], dateRange[1]);

      if (isLiveView) {
        intervalId = setInterval(() => {
          fetchData(dateRange[0], dateRange[1]);
        }, 60000);
      }
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [dateRange, isLiveView]);

  const disabledDate = (current) => {
    if (!dates || dates.length === 0) {
      return current && current > moment().endOf("day");
    }
    const tooLate = dates[0] && current.diff(dates[0], "days") > 13;
    const tooEarly = dates[0] && dates[0].diff(current, "days") > 13;
    const future = current && current > moment().endOf("day");
    return !!tooEarly || !!tooLate || !!future;
  };

  const onOpenChange = (open) => {
    if (open) {
      setDates([]);
    }
  };

  const handleDateChange = (val) => {
    setDateRange(val);
  };

  const handleShowDetails = (record) => {
    const stats = record.attendance;
    setSelectedDetails({
      date: record.date,
      day: record.day,
      stats: stats,
      detailsList: stats.details,
    });
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setSelectedDetails(null);
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      align: "center",
      render: (text, record) => (
        <div className="font-semibold">
          {moment(text).format("DD/MM/YYYY")} ({record.day})
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Button type="link" onClick={() => handleShowDetails(record)}>
          View Summary
        </Button>
      ),
    },
  ];

  const detailColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Check In",
      dataIndex: "check_in_time",
      key: "check_in_time",
      render: (t) => t || "-",
    },
    {
      title: "Check Out",
      dataIndex: "check_out_time",
      key: "check_out_time",
      render: (t) => t || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => {
        const status = (text || "").toLowerCase();
        let color = "blue";
        let label = text || "-";

        switch (status) {
          case "on_time":
            color = "green";
            label = "On Time";
            break;
          case "short_time":
            color = "gold";
            label = "Short Time";
            break;
          case "missed":
            color = "red";
            label = "Missed";
            break;
          case "leave":
            color = "red";
            label = "Leave";
            break;
          case "sick":
            color = "magenta";
            label = "Sick";
            break;
          case "permission":
            color = "blue";
            label = "Permission";
            break;
          case "half_day":
            color = "orange";
            label = "Half Day";
            break;
          case "waiting":
          case "":
            color = "default";
            label = "Waiting";
            break;
          case "holiday":
            color = "pink";
            label = "Holiday";
            break;
          default:
            color = "blue";
            label = status.charAt(0).toUpperCase() + status.slice(1);
            break;
        }

        return (
          <Tag color={color} className="capitalize">
            {label}
          </Tag>
        );
      },
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
    },
  ];

  return (
    <div className="p-8">
      <div className="p-4 text-right">
        <BackButton />
      </div>
      <div className="bg-white drop-shadow-md p-8 rounded-lg w-full">
        <div className="text-2xl text-orange-500 font-semibold py-4">
          Instructor Report Status
        </div>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <span className="text-base font-semibold">Select Date Range:</span>
            <RangePicker
              format="DD/MM/YYYY"
              value={dateRange}
              dates={dates}
              onCalendarChange={(val) => setDates(val)}
              onChange={handleDateChange}
              onOpenChange={onOpenChange}
              disabledDate={disabledDate}
              allowClear={false}
              style={{ width: 300 }}
            />
          </div>

          <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg border border-gray-200 shadow-sm">
            <span className="text-sm font-medium text-gray-700">
              <span className="mr-1">🔴</span> Live View:
            </span>
            <Switch
              size="small"
              checked={isLiveView}
              onChange={setIsLiveView}
              className={isLiveView ? "bg-green-500" : ""}
            />
            {isFetchingSub && isLiveView && (
              <div className="flex items-center ml-3 pl-3 border-l border-gray-200">
                <Spin size="small" className="mr-2" />
                <span className="text-xs text-blue-600 font-medium">
                  Updating...
                </span>
              </div>
            )}
          </div>
        </div>

        <Table
          rowKey="date"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={false}
        />
      </div>

      <Modal
        title={null}
        visible={isModalVisible}
        onCancel={handleModalCancel}
        footer={null}
        width={700}
      >
        {selectedDetails && (
          <div>
            <div className="text-lg font-bold mb-4 border-b pb-2">
              Summary: {moment(selectedDetails.date).format("DD/MM/YYYY")},{" "}
              {selectedDetails.day}
            </div>

            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-2 text-base">
                <div>
                  On Time:{" "}
                  <span className="font-semibold">
                    {selectedDetails.stats.onTime}
                  </span>
                </div>
                <div>
                  Missed:{" "}
                  <span className="font-semibold">
                    {selectedDetails.stats.missed}
                  </span>
                </div>
                <div>
                  Short Time:{" "}
                  <span className="font-semibold">
                    {selectedDetails.stats.shortTime}
                  </span>
                </div>
                <div>
                  Leave:{" "}
                  <span className="font-semibold">
                    {selectedDetails.stats.leave}
                  </span>
                </div>
                <div>
                  Sick:{" "}
                  <span className="font-semibold">
                    {selectedDetails.stats.sick}
                  </span>
                </div>
                <div>
                  Holiday:{" "}
                  <span className="font-semibold">
                    {selectedDetails.stats.holiday}
                  </span>
                </div>
              </div>
            </div>

            <div className="font-semibold mb-2">Details:</div>
            <Table
              dataSource={selectedDetails.detailsList}
              columns={detailColumns}
              pagination={false}
              size="small"
              rowKey="name"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default InstructorReportStatus;
